import { useTranslation } from 'next-i18next'

import ROUTES from '~/shared/constants/routes.const'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { Link } from '~/components/Link'

import styles from '~/shared/styles/Input.module.css'

import { Icon } from './Icon'
import { ButtonSize } from './Buttons/types'
import ResponsiveImage from './Image/ResponsiveImage'
import SVGIcon from './Icons/svgs/SVGIcon'
import SettingsIcon from './Icons/svgs/SettingsIcon'

const Placeholder = 'avatar-portraits/hom-default-avatar.png'

type AvatarType = 'chat' | 'dashboard' | 'settings' | 'session'

export default function Avatar2D({
  avatarPngUrl,
  showCreateAvatarButton = false,
  createAvatarButtonSize = 'medium',
  createAvatarButtonClasses = '',
  showEditAvatarIcon = false,
  editAvatarIcon = 'settingsIcon',
  sizeClassName = '',
  dimension = 150,
  btnHref = '',
  isS3Bucket = true,
  imgInnerClassName = '',
  currentLeague,
}: {
  avatarPngUrl?: string | null
  type?: AvatarType
  showCreateAvatarButton?: boolean
  createAvatarButtonSize?: ButtonSize
  createAvatarButtonClasses?: string
  showEditAvatarIcon?: boolean
  editAvatarIcon?: 'settingsIcon' | 'editIcon'
  sizeClassName?: string
  dimension?: number
  btnHref?: string
  isS3Bucket?: boolean
  imgInnerClassName?: string
  currentLeague?: string
}): React.ReactElement {
  const { t } = useTranslation('avatar')

  const srcKey = avatarPngUrl ?? Placeholder
  const href = btnHref || ROUTES.AVATAR
  const mediaQuery = [{ screen: '(min-width: 280px)', width: `${dimension}px` }]
  return (
    <div className={`relative flex flex-col ${sizeClassName}`}>
      {!avatarPngUrl && showCreateAvatarButton && (
        <div className="absolute inset-0 z-10">
          <div className="inset-center">
            <ButtonPrimary href={href} link="internal" size={createAvatarButtonSize}>
              <div className={createAvatarButtonClasses}>{t('createAvatarButton')}</div>
            </ButtonPrimary>
          </div>
        </div>
      )}
      {currentLeague && (
        <div className="absolute self-end -mr-4 -mt-1.5">
          <ResponsiveImage alt={t('currentLeague')} height={48} srcKey={currentLeague} width={35} />
        </div>
      )}
      <div className={`${editAvatarIcon === 'editIcon' ? 'flex flex-col items-center' : ''}`}>
        {editAvatarIcon === 'settingsIcon' && avatarPngUrl && showEditAvatarIcon && (
          <Link className="absolute right-0 bottom-0 opacity-75 hover:opacity-100 cursor-pointer" to={href}>
            <SVGIcon icon={SettingsIcon} pathClassName="text-black" size={24} />
          </Link>
        )}

        {isS3Bucket ? (
          <ResponsiveImage
            alt="User avatar"
            height={dimension}
            imageClassName={`rounded-full ${imgInnerClassName}`}
            imageWidths={`${dimension}`}
            mediaQuery={mediaQuery}
            srcKey={srcKey}
            width={dimension}
          />
        ) : (
          <ResponsiveImage
            alt="User avatar"
            height={dimension}
            imageClassName={`rounded-full ${imgInnerClassName}`}
            src={srcKey}
            width={dimension}
          />
        )}

        {editAvatarIcon === 'editIcon' && avatarPngUrl && showEditAvatarIcon && (
          <Link className="opacity-75 hover:opacity-100 cursor-pointer" to={href}>
            <div className="flex justify-center">
              <div className="font-medium text-sm mr-3">{t('editAvatar')}</div>
              <div className="mt-7 ml-5">
                <Icon className={`${styles['input-edit-icon']}`} customImageSrc="misc/edit-input-field.svg" />
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  )
}
